import React, { useCallback } from 'react';
import { Text } from '@/components/Text';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { ICharacter } from '@/models/characters';
import { default_avatar } from '@/assets/remote-assets';
import { cn } from '@/lib/utils';
import { SkeletonImage } from '@/components/SkeletonImage';
import posthog from 'posthog-js';

type Props = {
  character: ICharacter;
  className?: string;
  imageCount?: number;
  onCardClick?: () => void;
};

export const CollectionCharacterCard: React.FC<Props> = ({ character, className, onCardClick, imageCount }) => {
  const { push } = useRouter();
  const [avatarUrl, setAvatarUrl] = useState(character.avatar_urls && character.avatar_urls.length > 0 ? character.avatar_urls[0] : character.avatar_url || default_avatar);

  const handleCardClick = useCallback(() => {
    if (onCardClick) {
      onCardClick();
    } else {
      if (character.unique_code && character.seo_slug) {
        push(`/images/collection/${character.unique_code}/${character.seo_slug}`);
      } else {
        push(`/images/collection-redirect/${character.id}`);
      }
      posthog?.capture('view_gallery_click', {character_id: character.id});
    }
  }, [character.id, character.unique_code, character.seo_slug, onCardClick, push]);

  return (
    <div
      className={cn('group relative rounded-xl cursor-pointer h-[400px] lg:h-[400px] xl:h-[400px]', className)}
      onClick={handleCardClick}
    >
      <div className="absolute left-[3px] top-[3px] h-full w-full bg-black-100 rounded-[15px] z-0" />
      <div className={cn("flex relative h-full max-h-full max-w-full z-1 overflow-hidden rounded-[inherit] border-2 border-black", 
                        "group-hover:border-pink left-[0] top-[0] group-active:left-[1px] group-active:top-[1px] transition-all duration-200", 
                        )}>
        <SkeletonImage
          className={cn(
            'z-0 absolute left-0 top-0 w-full h-auto min-h-[400px] pointer-events-none object-cover rounded-[10px]',
          )}
          height={0}
          width={0}
          sizes="(max-width: 768px) 50vw, 400px"
          src={avatarUrl}
          alt={character.name}
          priority={true}
        />
        <div className="absolute top-0 left-0 bg-card-gr w-full h-full" />
        <div className="w-full px-3 pb-3 z-10 relative flex flex-col flex-1 justify-end">
          <Text className="mb-0.5" variant="h3" textColor="white">
            {character.name}
          </Text>
          <div className="flex flex-wrap gap-2 max-w-[calc(100%-1rem)]">
              <div className="bg-base-100 rounded-lg px-2 py-2 flex ml-[-2px] items-center z-10 w-max">
                <Text variant="body-1" className="text-white whitespace-nowrap">{imageCount} images</Text>
              </div>
          </div>
        </div>
      </div>
    </div>
  );
};
